import { parseContract } from '@/lib/api/parse';

import { realtimeEventSchemas, type RealtimeEventName, type RealtimeEventPayload } from './events';

export type RealtimeClient = {
  /** Subscribe to a typed event. Returns the unsubscribe. */
  on: <E extends RealtimeEventName>(
    event: E,
    handler: (payload: RealtimeEventPayload<E>) => void,
  ) => () => void;
  /** Feed one raw wire event in. Validates, then fans out. Never throws. */
  receive: (name: string, payload: unknown) => void;
};

type ErasedHandler = (payload: unknown) => void;

export function createRealtimeClient(): RealtimeClient {
  const handlers = new Map<string, Set<ErasedHandler>>();

  return {
    on: (event, handler) => {
      const set = handlers.get(event) ?? new Set<ErasedHandler>();
      handlers.set(event, set);
      set.add(handler as ErasedHandler);
      return () => {
        set.delete(handler as ErasedHandler);
      };
    },

    receive: (name, payload) => {
      if (!(name in realtimeEventSchemas)) return;
      const schema = realtimeEventSchemas[name as RealtimeEventName];

      let parsed: unknown;
      try {
        // Reuses the API layer's drift signal: a bad payload lands in Sentry as
        // contract_drift rather than throwing back into the socket callback.
        parsed = parseContract(schema, payload, `realtime:${name}`);
      } catch {
        return;
      }

      handlers.get(name)?.forEach((handler) => handler(parsed));
    },
  };
}
